import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  PrinterIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useToast } from '../components/ToastContainer';
import { format } from 'date-fns';
import { zhTW } from 'date-fns/locale';
import * as globalWorkLogApi from '../services/api/globalWorkLog';
import MeetingDecisionReport from '../components/worklog/MeetingDecisionReport';

interface GlobalWorkLog {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  isGlobal: boolean;
  organizationName?: string;
  managementCompany?: string;
  dutyStaff?: string;
  staffAttendance?: any;
  equipmentStatus?: any;
  otherWorkItems?: any;
  createdAt: string;
  updatedAt: string;
  createdBy: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  selectedIssues: Array<{
    id: string;
    sortOrder: number;
    issue: {
      id: string;
      key: string;
      title: string;
      description?: string;
      status: string;
      priority: string;
      assignee?: {
        id: string;
        name: string;
        email: string;
        avatar?: string;
      };
      reporter: {
        id: string;
        name: string;
        email: string;
        avatar?: string;
      };
      project: {
        id: string;
        name: string;
        key: string;
      };
      boardStatus?: {
        id: string;
        name: string;
        color: string;
      };
      comments: Array<{
        id: string;
        content: string;
        createdAt: string;
        author: {
          id: string;
          name: string;
          email: string;
          avatar?: string;
        };
      }>;
    };
  }>;
}

const GlobalWorkLogExport: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const [workLog, setWorkLog] = useState<GlobalWorkLog | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (id) {
      loadWorkLog();
    }
  }, [id]);

  const loadWorkLog = async () => {
    try {
      setIsLoading(true);
      const data = await globalWorkLogApi.getGlobalWorkLog(id!);
      setWorkLog(data);
    } catch (error) {
      console.error('Failed to load global work log:', error);
      showError('載入全域工作日誌失敗');
      navigate('/global-worklogs');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'yyyy/MM/dd', { locale: zhTW });
  };

  const formatDateTime = (dateString: string) => {
    return format(new Date(dateString), 'yyyy/MM/dd HH:mm', { locale: zhTW });
  };

  const handlePrint = () => {
    window.print();
  };

  const exportToPDF = async () => {
    try {
      setIsExporting(true);
      
      // 使用瀏覽器的列印功能來生成 PDF
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        showError('無法開啟列印視窗，請檢查瀏覽器彈出視窗設定');
        return;
      }

      const printContent = printRef.current?.innerHTML || '';
      const printStyles = `
        <style>
          @media print {
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
            .no-print { display: none !important; }
            .page-break { page-break-before: always; }
            .avoid-break { page-break-inside: avoid; }
          }
          /* 基本樣式 */
          body { 
            margin: 0; 
            padding: 20px; 
            font-size: 12px; 
            line-height: 1.5; 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans TC', 'Microsoft JhengHei', sans-serif;
            word-wrap: break-word;
            word-break: normal;
          }
          
          /* 圖示大小修正 */
          svg {
            width: 16px !important;
            height: 16px !important;
            display: inline-block !important;
            vertical-align: middle !important;
          }
          
          /* 標題和段落間距 */
          h1, h2, h3, h4, h5, h6 { 
            margin-top: 1em; 
            margin-bottom: 0.5em; 
            line-height: 1.3;
          }
          p { 
            margin-bottom: 0.8em; 
            line-height: 1.5;
          }
          
          /* 區塊元素 */
          .header { margin-bottom: 20px; padding-bottom: 10px; border-bottom: 2px solid #333; }
          .title { font-size: 20px; font-weight: bold; margin-bottom: 5px; }
          .subtitle { color: #666; font-size: 14px; }
          .section { margin-bottom: 20px; }
          .section-title { font-size: 16px; font-weight: bold; margin-bottom: 10px; color: #333; }
          
          /* 資訊網格 */
          .info-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; margin-bottom: 15px; }
          .info-item { display: flex; align-items: center; }
          .info-label { font-weight: 500; margin-right: 8px; min-width: 80px; white-space: nowrap; }
          .info-value { color: #666; word-break: break-word; }
          
          /* 設備網格 */
          .equipment-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; }
          .equipment-item { border: 1px solid #ddd; padding: 8px; border-radius: 4px; }
          .equipment-label { font-weight: 500; margin-bottom: 4px; }
          
          /* 狀態標籤 */
          .status-normal { background: #dcfce7; color: #166534; padding: 2px 6px; border-radius: 3px; font-size: 11px; display: inline-block; }
          .status-abnormal { background: #fecaca; color: #dc2626; padding: 2px 6px; border-radius: 3px; font-size: 11px; display: inline-block; }
          
          /* 其他元素 */
          .notes { margin-top: 6px; font-size: 11px; color: #666; line-height: 1.4; }
          .meeting-section { margin-top: 30px; }
          
          /* 列表樣式 */
          ul, ol { 
            margin-left: 1.5em; 
            margin-bottom: 0.8em;
          }
          li { 
            margin-bottom: 0.3em;
            line-height: 1.5;
          }
          
          /* 表格樣式 */
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 1em;
          }
          th, td { 
            padding: 8px; 
            text-align: left; 
            border: 1px solid #ddd;
          }
          th { 
            background-color: #f5f5f5; 
            font-weight: 600;
          }
          
          /* Flex 容器中的圖示 */
          .flex { display: flex; }
          .items-center { align-items: center; }
          .flex svg { margin-right: 4px; }
        </style>
      `;

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>${workLog?.title} - 工作日誌匯出</title>
          ${printStyles}
        </head>
        <body>
          ${printContent}
        </body>
        </html>
      `);
      
      printWindow.document.close();
      printWindow.focus();
      
      // 等待圖片和樣式載入
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 1000);

      showSuccess('PDF 匯出視窗已開啟');
    } catch (error) {
      console.error('Export to PDF error:', error);
      showError('PDF 匯出失敗');
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!workLog) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">找不到工作日誌</h3>
        <p className="mt-1 text-sm text-gray-500">請確認工作日誌是否存在或您是否有權限存取</p>
        <div className="mt-6">
          <button
            onClick={() => navigate('/global-worklogs')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            返回列表
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-4">
        {/* 操作欄 */}
        <div className="flex items-center justify-between mb-6 no-print bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(`/global-worklogs/${workLog.id}`)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">匯出工作日誌</h1>
              <p className="text-gray-600 mt-1">{workLog.title}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="inline-flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <PrinterIcon className="h-4 w-4 mr-2" />
              列印預覽
            </button>
            
            <button
              onClick={exportToPDF}
              disabled={isExporting}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
              {isExporting ? '匯出中...' : '匯出 PDF'}
            </button>
          </div>
        </div>

      {/* 可列印內容 */}
      <div ref={printRef} className="bg-white p-8 rounded-lg shadow-sm border">
        {/* 標題頁 */}
        <div className="mb-8 pb-4 border-b-2 border-gray-300">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{workLog.title}</h1>
          <p className="text-gray-600">
            全域工作日誌 • 匯出時間：{formatDateTime(new Date().toISOString())}
          </p>
        </div>

        {/* 基本資訊 */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">基本資訊</h2>
          <table className="w-full border-collapse border border-gray-300 mb-4">
            <tbody>
              {workLog.organizationName && (
                <tr>
                  <td className="border border-gray-300 px-4 py-2 bg-gray-50 font-medium text-gray-700 w-1/4">組織名稱</td>
                  <td className="border border-gray-300 px-4 py-2 text-gray-600">{workLog.organizationName}</td>
                </tr>
              )}
              <tr>
                <td className="border border-gray-300 px-4 py-2 bg-gray-50 font-medium text-gray-700 w-1/4">報告期間</td>
                <td className="border border-gray-300 px-4 py-2 text-gray-600">{formatDate(workLog.startDate)} - {formatDate(workLog.endDate)}</td>
              </tr>
              {workLog.dutyStaff && (
                <tr>
                  <td className="border border-gray-300 px-4 py-2 bg-gray-50 font-medium text-gray-700 w-1/4">值班人員</td>
                  <td className="border border-gray-300 px-4 py-2 text-gray-600">{workLog.dutyStaff}</td>
                </tr>
              )}
              {workLog.managementCompany && (
                <tr>
                  <td className="border border-gray-300 px-4 py-2 bg-gray-50 font-medium text-gray-700 w-1/4">管理公司</td>
                  <td className="border border-gray-300 px-4 py-2 text-gray-600">{workLog.managementCompany}</td>
                </tr>
              )}
              <tr>
                <td className="border border-gray-300 px-4 py-2 bg-gray-50 font-medium text-gray-700 w-1/4">狀態</td>
                <td className="border border-gray-300 px-4 py-2 text-gray-600">
                  {workLog.status === 'PUBLISHED' ? '已發布' : 
                   workLog.status === 'DRAFT' ? '草稿' : '已封存'}
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 bg-gray-50 font-medium text-gray-700 w-1/4">建立者</td>
                <td className="border border-gray-300 px-4 py-2 text-gray-600">{workLog.createdBy.name}</td>
              </tr>
              {workLog.staffAttendance?.totalStaff && (
                <tr>
                  <td className="border border-gray-300 px-4 py-2 bg-gray-50 font-medium text-gray-700 w-1/4">總人數</td>
                  <td className="border border-gray-300 px-4 py-2 text-gray-600">{workLog.staffAttendance.totalStaff} 人</td>
                </tr>
              )}
              {workLog.staffAttendance?.presentStaff && (
                <tr>
                  <td className="border border-gray-300 px-4 py-2 bg-gray-50 font-medium text-gray-700 w-1/4">出勤人數</td>
                  <td className="border border-gray-300 px-4 py-2 text-gray-600">{workLog.staffAttendance.presentStaff} 人</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 設備狀況 */}
        {workLog.equipmentStatus && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">設備狀況</h2>
            
            {/* 固定系統 */}
            <table className="w-full border-collapse border border-gray-300 mb-4">
              <thead>
                <tr>
                  <th className="border border-gray-300 px-4 py-2 bg-gray-100 text-left font-medium text-gray-900">系統名稱</th>
                  <th className="border border-gray-300 px-4 py-2 bg-gray-100 text-left font-medium text-gray-900">狀態</th>
                  <th className="border border-gray-300 px-4 py-2 bg-gray-100 text-left font-medium text-gray-900">備註</th>
                </tr>
              </thead>
              <tbody>
                {(workLog.equipmentStatus.elevatorSystem?.status || workLog.equipmentStatus.elevatorSystem) && (
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-medium text-gray-900">電梯系統</td>
                    <td className="border border-gray-300 px-4 py-2">
                      <span className={`inline-block px-2 py-1 text-xs rounded ${
                        (workLog.equipmentStatus.elevatorSystem?.status || workLog.equipmentStatus.elevatorSystem) === '正常' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {workLog.equipmentStatus.elevatorSystem?.status || workLog.equipmentStatus.elevatorSystem}
                      </span>
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-gray-600">
                      {workLog.equipmentStatus.elevatorSystem?.notes || '-'}
                    </td>
                  </tr>
                )}
                
                {(workLog.equipmentStatus.waterPumpSystem?.status || workLog.equipmentStatus.waterPumpSystem) && (
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-medium text-gray-900">供水系統</td>
                    <td className="border border-gray-300 px-4 py-2">
                      <span className={`inline-block px-2 py-1 text-xs rounded ${
                        (workLog.equipmentStatus.waterPumpSystem?.status || workLog.equipmentStatus.waterPumpSystem) === '正常' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {workLog.equipmentStatus.waterPumpSystem?.status || workLog.equipmentStatus.waterPumpSystem}
                      </span>
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-gray-600">
                      {workLog.equipmentStatus.waterPumpSystem?.notes || '-'}
                    </td>
                  </tr>
                )}
                
                {(workLog.equipmentStatus.powerSystem?.status || workLog.equipmentStatus.powerSystem) && (
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-medium text-gray-900">電力系統</td>
                    <td className="border border-gray-300 px-4 py-2">
                      <span className={`inline-block px-2 py-1 text-xs rounded ${
                        (workLog.equipmentStatus.powerSystem?.status || workLog.equipmentStatus.powerSystem) === '正常' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {workLog.equipmentStatus.powerSystem?.status || workLog.equipmentStatus.powerSystem}
                      </span>
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-gray-600">
                      {workLog.equipmentStatus.powerSystem?.notes || '-'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* 自定義系統 */}
            {workLog.equipmentStatus.customSystems && workLog.equipmentStatus.customSystems.length > 0 && (
              <div className="mt-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">自定義系統</h3>
                <table className="w-full border-collapse border border-gray-300 mb-4">
                  <thead>
                    <tr>
                      <th className="border border-gray-300 px-4 py-2 bg-gray-100 text-left font-medium text-gray-900">系統名稱</th>
                      <th className="border border-gray-300 px-4 py-2 bg-gray-100 text-left font-medium text-gray-900">狀態</th>
                      <th className="border border-gray-300 px-4 py-2 bg-gray-100 text-left font-medium text-gray-900">備註</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workLog.equipmentStatus.customSystems.map((system: any, index: number) => (
                      <tr key={index}>
                        <td className="border border-gray-300 px-4 py-2 font-medium text-gray-900">{system.name}</td>
                        <td className="border border-gray-300 px-4 py-2">
                          <span className={`inline-block px-2 py-1 text-xs rounded ${
                            system.status === '正常' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {system.status}
                          </span>
                        </td>
                        <td className="border border-gray-300 px-4 py-2 text-gray-600">
                          {system.notes || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* 社區消防 */}
            {workLog.equipmentStatus.communityFireSafety && (
              <div className="mt-4">
                <h3 className="text-lg font-medium text-red-800 mb-3 flex items-center">
                  <span className="mr-2">🔥</span> 社區消防
                </h3>
                <table className="w-full border-collapse border border-red-300 mb-4 bg-red-50">
                  <tbody>
                    {workLog.equipmentStatus.communityFireSafety.totalIsolations && (
                      <tr>
                        <td className="border border-red-300 px-4 py-2 bg-red-100 font-medium text-gray-700 w-1/4">總共隔離</td>
                        <td className="border border-red-300 px-4 py-2 text-gray-900 font-semibold">{workLog.equipmentStatus.communityFireSafety.totalIsolations} 處</td>
                      </tr>
                    )}
                    {workLog.equipmentStatus.communityFireSafety.todayNewIsolations && (
                      <tr>
                        <td className="border border-red-300 px-4 py-2 bg-red-100 font-medium text-gray-700 w-1/4">今日新增</td>
                        <td className="border border-red-300 px-4 py-2 text-gray-900 font-semibold">{workLog.equipmentStatus.communityFireSafety.todayNewIsolations} 處</td>
                      </tr>
                    )}
                    {workLog.equipmentStatus.communityFireSafety.todayRepaired && (
                      <tr>
                        <td className="border border-red-300 px-4 py-2 bg-red-100 font-medium text-gray-700 w-1/4">今日修復</td>
                        <td className="border border-red-300 px-4 py-2 text-gray-900 font-semibold">{workLog.equipmentStatus.communityFireSafety.todayRepaired} 處</td>
                      </tr>
                    )}
                    {workLog.equipmentStatus.communityFireSafety.status && (
                      <tr>
                        <td className="border border-red-300 px-4 py-2 bg-red-100 font-medium text-gray-700 w-1/4">消防狀態</td>
                        <td className="border border-red-300 px-4 py-2">
                          <span className={`inline-block px-2 py-1 text-xs rounded ${
                            workLog.equipmentStatus.communityFireSafety.status === '正常' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {workLog.equipmentStatus.communityFireSafety.status}
                          </span>
                        </td>
                      </tr>
                    )}
                    {workLog.equipmentStatus.communityFireSafety.notes && (
                      <tr>
                        <td className="border border-red-300 px-4 py-2 bg-red-100 font-medium text-gray-700 w-1/4">消防備註</td>
                        <td className="border border-red-300 px-4 py-2 text-gray-600">{workLog.equipmentStatus.communityFireSafety.notes}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {workLog.equipmentStatus.notes && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-gray-700 mb-1">整體備註</div>
                <div className="text-sm text-gray-600">{workLog.equipmentStatus.notes}</div>
              </div>
            )}
          </div>
        )}

        {/* 其他工作項目 */}
        {workLog.otherWorkItems && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">其他工作項目</h2>
            <table className="w-full border-collapse border border-gray-300 mb-4">
              <thead>
                <tr>
                  <th className="border border-gray-300 px-4 py-2 bg-gray-100 text-left font-medium text-gray-900 w-1/4">工作項目</th>
                  <th className="border border-gray-300 px-4 py-2 bg-gray-100 text-left font-medium text-gray-900">內容描述</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(workLog.otherWorkItems).map(([key, value]) => {
                  if (!value) return null;
                  
                  const labels: Record<string, string> = {
                    cleaningWork: '清潔工作',
                    maintenanceWork: '維修工作',
                    securityPatrol: '安全巡邏',
                    visitorManagement: '訪客管理',
                    emergencyHandling: '緊急處理',
                    otherTasks: '其他任務'
                  };
                  
                  return (
                    <tr key={key}>
                      <td className="border border-gray-300 px-4 py-2 bg-gray-50 font-medium text-gray-900">{labels[key]}</td>
                      <td className="border border-gray-300 px-4 py-2 text-gray-600 whitespace-pre-wrap align-top">{value as string}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* 會議決議執行情形報告 */}
        {workLog.selectedIssues.length > 0 && (
          <div className="mt-8 pt-8 border-t-2 border-gray-200">
            <MeetingDecisionReport workLogId={workLog.id} isGlobal={true} />
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default GlobalWorkLogExport;
